import { useI18n, type MessageKey } from '@ecom/i18n';
import { Badge, Card, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

/**
 * `tenantStatus` comes from `/auth/me/memberships` (OQ-23, already in
 * session via `useActiveShop()`) — the SAME field the public storefront's
 * own visibility rule (`app.store_is_publicly_visible()`) is driven by.
 * No new read: this card is a client-side interpretation of data already
 * fetched for the shop switcher.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'good',
  pending: 'warn',
  suspended: 'critical',
  refused: 'critical',
};

const STATUS_LABEL_KEY: Record<string, MessageKey> = {
  active: 'merchant.store.visibility.active',
  pending: 'merchant.store.visibility.pending',
  suspended: 'merchant.store.visibility.suspended',
  refused: 'merchant.store.visibility.refused',
};

export function StoreVisibilityCard({
  tenantStatus,
  storeSlug,
}: {
  tenantStatus: string;
  storeSlug: string | null;
}): ReactElement {
  const { t } = useI18n();
  const publicPath = storeSlug !== null ? `/shop/${storeSlug}` : null;
  const statusLabelKey = STATUS_LABEL_KEY[tenantStatus];

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-ink">{t('merchant.store.visibility.label')}</span>
        <Badge tone={STATUS_TONE[tenantStatus] ?? 'neutral'}>
          {statusLabelKey !== undefined ? t(statusLabelKey) : tenantStatus}
        </Badge>
      </div>
      {tenantStatus === 'active' && publicPath !== null ? (
        <p className="text-xs text-inkFaint">
          {t('merchant.store.visibility.publicCatalog')}{' '}
          <span className="font-mono text-ink">{publicPath}</span>
        </p>
      ) : null}
    </Card>
  );
}
